import React, { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AdminAppShell } from "../../../design-system/shells/AdminAppShell";
import { useAuth } from "../../../lib/auth";
import { api } from "../../../lib/api";
import StatusChip from "../../../components/StatusChip";
import { ProjectForm } from "../../../components/portfolio/ProjectForm";
import { MediaUploader } from "../../../components/portfolio/MediaUploader";
import { MediaCard } from "../../../components/portfolio/MediaCard";
import { ReviewersPanel } from "../../../components/portfolio/ReviewersPanel";
import { CommentsPanel } from "../../../components/portfolio/CommentsPanel";
import { PublishControls } from "../../../components/portfolio/PublishControls";
import { Section } from "../../../components/portfolio/Section";
import { usePortfolioProject } from "./usePortfolioProject";
import { errMsg } from "../../../lib/portfolio";

const EDITABLE = ["draft", "in_review", "approved", "unpublished"];

function Activity({ projectId, reloadKey }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/admin/portfolio/projects/${projectId}/audit`).then((r) => setRows(r.data.items)).catch(() => {});
  }, [projectId, reloadKey]);
  return (
    <ul className="space-y-1 max-h-72 overflow-y-auto" data-testid="portfolio-activity-list">
      {rows.map((r) => (
        <li key={r.id} className="text-xs font-mono text-erf-dark/80 ds-text-safe">
          {new Date(r.created_at).toLocaleString()} · {r.action.replace(/_/g, " ")} · {r.actor_role}
        </li>
      ))}
    </ul>
  );
}

export default function PortfolioDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { project, media, error, reload } = usePortfolioProject(id);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const refresh = () => { reload(); setTick((t) => t + 1); };

  if (project && project.my_role === "reviewer") return <Navigate to={`/admin/portfolio/${id}/review`} replace />;
  const save = async (payload) => {
    setBusy(true);
    try { await api.patch(`/admin/portfolio/projects/${id}`, payload); toast.success("Saved."); refresh(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const editable = project && EDITABLE.includes(project.status);
  return (
    <AdminAppShell activeNav="portfolio" title={project?.title || "Portfolio project"} user={user}
      subtitle={project ? `Public link name: ${project.slug}` : ""}
      breadcrumbs={[{ label: "Portfolio Manager", to: "/admin/portfolio" }, { label: project?.title || "Project", to: null }]}>
      {error && <p className="text-sm font-nav text-[#991B1B]" role="alert" data-testid="portfolio-detail-error">{error}</p>}
      {project && (
        <div className="space-y-5" data-testid="portfolio-detail">
          <Section title="Status and publishing" testId="portfolio-status-section"
            hint="Draft → In review → Approved → Published. Only you can publish."
            actions={<StatusChip status={project.status} testId="portfolio-detail-status" />}>
            <PublishControls project={project} onChanged={refresh} />
          </Section>
          <Section title="Photos and videos" testId="portfolio-media-section"
            hint="Originals are kept private and unchanged. Public copies have location and camera data removed.">
            {editable && <MediaUploader projectId={id} onUploaded={refresh} />}
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="portfolio-media-grid">
              {media.map((m) => (
                <MediaCard key={`${m.id}-${m.processing_state}-${m.caption}-${m.alt_text}`} media={m} founder
                  isCover={project.cover_media_id === m.id} onChanged={refresh} />
              ))}
            </div>
            {media.length === 0 && <p className="text-sm font-nav text-erf-dark/70">No files yet.</p>}
          </Section>
          <Section title="Project details" testId="portfolio-details-section"
            hint={editable ? undefined : "Unpublish the project to edit its details."}>
            <ProjectForm key={project.updated_at} initial={project} onSubmit={save} busy={busy} disabled={!editable} />
          </Section>
          <Section title="Reviewers" testId="portfolio-reviewers-section" hint="Reviewers can view, comment, and approve or request changes. They cannot publish.">
            <ReviewersPanel projectId={id} />
          </Section>
          <Section title="Comments and review decisions" testId="portfolio-comments-section">
            <CommentsPanel projectId={id} reloadKey={tick} />
          </Section>
          <Section title="Activity" testId="portfolio-activity-section" hint="Every change is recorded permanently.">
            <Activity projectId={id} reloadKey={tick} />
          </Section>
        </div>
      )}
    </AdminAppShell>
  );
}
